// src/components/Ajuda/Montador/composables/useBuilderState.js
// Singleton de estado do Montador (Vue 3)
// Mantém o shape original de dados: coordenadas em metros (xm, ym), ângulo em graus (rot), z-index (z)
// e overrides locais em o.local.{thicknesses, heights, gaps, wm, hm, dir}

import { reactive, computed, watch } from 'vue'
import { formatListSemicolon, parseNumberList } from '@/components/Ajuda/Montador/lib/numbers'

// ---------- Helpers ----------
let _autoId = 1
const genId = () => Date.now() + (_autoId++)
const asArray = (v) => Array.isArray(v) ? v : (v != null ? [v] : [])

// ---------- Params (defaults editáveis de cada tipo de obstáculo) ----------
export const params = reactive({
  visual: {
    rulersInsetPx: 12,
  },
  obstacles: {
    // Ex.: vertical: { wm: 3.00, hm: 0.50, dir: 1, barThickness: 0.10, thicknesses:[0.10], heights:[1.10,1.20], gaps:[...] }
    // Mantemos vazio aqui para herdar do que já existe no seu projeto;
    // o getObsSpecs usa fallbacks seguros se algo não vier.
  },
})

// ---------- State principal ----------
export const state = reactive({
  // View/escala
  scale: 17,                 // px por metro (o desenho usa metros; conversão feita no componente)
  view:  { scale: 1, x: 0, y: 0 },

  // Arena
  arenaMode: 'rect',         // 'rect' | 'quad' | 'free'
  free: { showHandles: false, verts: [] },

  // Obstáculos
  obstacles: [],             // [{ id, type, xm, ym, rot, z, seq?, local? }]
  selectedId: null,

  // Distâncias específicas por combo (face-a-face entre adjacentes)
  // Ex.: comboMeta[3].faces = [7.5] (duplo) ou [7.5, 7.5] (triplo)
  comboMeta: {}, 

  // Campos de edição (UI) — strings
  editLists: { thicks: '', heights: '', gaps: '' },

  // Ferramentas (precisam existir no estado inicial para evitar undefined)
  tools: {
    measureActive: false,
    measurePts: [],

    // os abaixo serão sobrescritos em MontadorPistaHipismo.vue (Object.assign),
    // mas mantemos defaults seguros aqui para evitar undefined em qualquer render
    pathActive: false,
    course: [],
    clickMode: 'montar',
    pathTension: 0.5,
    curveSmoothness: 0.5,
    exitStrides: 3,
    entryStrides: 3,
    rMin: 8,
  },

})

export function getComboFaces(groupId, gapsCount = 1) {
  const def = Number(params?.combos?.defaultCentro ?? 7.5)
  const entry = state.comboMeta?.[groupId]
  const faces = Array.isArray(entry?.faces) ? entry.faces.slice(0, gapsCount) : []
  while (faces.length < gapsCount) faces.push(def)
  return faces
}

export function setComboFaces(groupId, faces = []) {
  if (!groupId) return
  if (!state.comboMeta) state.comboMeta = {}
  state.comboMeta[groupId] = { faces: faces.map(Number) }
}

export function reflowComboFromChanged(changed) {
  if (!changed?.comboGroup) return
  const parts = state.obstacles.filter(o => o.comboGroup === changed.comboGroup)
  if (parts.length < 2) return

  // eixo do combo: normal da frente do item alterado
  const specC = getObsSpecs(changed)
  const ang = (changed.rot - 90) * Math.PI / 180
  const sign = (specC.dir === -1 ? -1 : 1)
  const n = { x: Math.cos(ang) * sign, y: Math.sin(ang) * sign }
  const proj = (o) => o.xm * n.x + o.ym * n.y
  const ordered = parts.slice().sort((a,b) => proj(a) - proj(b))
  const idx = ordered.findIndex(p => p.id === changed.id)
  if (idx < 0) return

  const gapsCount = ordered.length - 1
  const faces = getComboFaces(changed.comboGroup, gapsCount)

  const sum = (arr) => (Array.isArray(arr) ? arr.reduce((a,b)=> a+(+b||0), 0) : 0)
  const needCC = (A, B, iPair) => {
    const sA = getObsSpecs(A), sB = getObsSpecs(B)
    const offA = (sum(sA.thicknesses) + sum(sA.gaps)) / 2
    const offB = (sum(sB.thicknesses) + sum(sB.gaps)) / 2
    return faces[iPair] + offA + offB
  }

  // para frente (+n)
  for (let j = idx + 1; j < ordered.length; j++) {
    const P = ordered[j - 1]
    const Q = ordered[j]
    const dcc = needCC(P, Q, j - 1)
    Q.xm = P.xm + n.x * dcc
    Q.ym = P.ym + n.y * dcc
  }
  // para trás (−n)
  for (let j = idx - 1; j >= 0; j--) {
    const Q = ordered[j + 1]
    const P = ordered[j]
    const dcc = needCC(P, Q, j)
    P.xm = Q.xm - n.x * dcc
    P.ym = Q.ym - n.y * dcc
  }
}

// ---------- Selecionado ----------
export const selected = computed(() =>
  state.obstacles.find(o => o.id === state.selectedId) || null
)

// ---------- Especificações efetivas do obstáculo ----------
export function getObsSpecs(o) {
  if (!o) return { wm: 0, hm: 0, dir: 1, thicknesses: [], heights: [], gaps: [] }
  const base = params.obstacles?.[o.type] || {}

  // Largura/altura externas para posicionamento/box do obstáculo
  const wm  = o.local?.wm  ?? base.wm  ?? base.widthM  ?? 1.60
  const dir = o.local?.dir ?? base.dir ?? 1

  // Listas estruturais (mantém comportamento original de fallback)
  const thicknesses = (o.local?.thicknesses?.length
    ? o.local.thicknesses
    : (base.thicknesses ?? (base.barThickness ? [base.barThickness] : [])))

  const heights = (o.local?.heights?.length
    ? o.local.heights
    : (base.heights ?? base.barHeights ?? []))

  const gaps = (o.local?.gaps?.length
    ? o.local.gaps
    : (base.gaps ?? []))

  // --- HM automático ---
  // Base: o que vier definido (local ou preset)
  const hmBase =
    (o.local?.hm ?? base.hm ?? base.heightM ??
      (Array.isArray(thicknesses) && thicknesses.length ? thicknesses[0] : (base.barThickness ?? 0.10)))

  // Para Oxer/Triple, o hm acompanha a “profundidade” do conjunto:
  // somatório das espessuras + vãos. Para os demais, mantemos hmBase.
  const sum = (arr) => (Array.isArray(arr) ? arr.reduce((a,b)=> a + (Number(b)||0), 0) : 0)
  const isMulti = String(o.type).toLowerCase() === 'oxer'
                || String(o.type).toLowerCase() === 'triplebar'
                || String(o.type).toLowerCase() === 'triple'
  const hmStack = sum(thicknesses) + sum(gaps)
  const hm = isMulti ? Math.max(hmBase, hmStack) : hmBase
  // --- fim HM automático ---

  return { wm, hm, dir, thicknesses, heights, gaps, barThickness: base.barThickness }
}

// ---------- Ações básicas ----------
export function select(id) {
  state.selectedId = id ?? null
}

export function clearSelection() {
  state.selectedId = null
}

export function addObstacle(partial = {}) {
  const baseZ = state.obstacles.reduce((m, o) => Math.max(m, o.z ?? 0), 0)
  const o = {
    id: genId(),
    type: partial.type ?? 'vertical',
    xm: Number(partial.xm ?? 5),
    ym: Number(partial.ym ?? 5),
    rot: Number(partial.rot ?? 0),
    z: Number(partial.z ?? (baseZ + 1)),
    seq: partial.seq ?? null,
    local: partial.local ? { ...partial.local } : undefined,
  }
  state.obstacles.push(o)
  state.selectedId = o.id
  return o
}

export function removeSelected() {
  if (!selected.value) return
  const idx = state.obstacles.findIndex(o => o.id === state.selectedId)
  if (idx >= 0) state.obstacles.splice(idx, 1)
  state.selectedId = null
}

export function cloneSelected() {
  if (!selected.value) return null
  const s = selected.value
  const copy = addObstacle({
    ...s,
    id: undefined,
    xm: s.xm + 1, // desloca 1m para evitar sobreposição exata
    local: s.local ? { ...s.local } : undefined,
  })
  return copy
}

// ---------- Movimento/rotação (em metros e graus) ----------
export function moveSelectedBy(dxM = 0, dyM = 0) {
  if (!selected.value) return
  selected.value.xm += dxM
  selected.value.ym += dyM
}

export function moveSelectedByPx(dxPx = 0, dyPx = 0) {
  const k = 1 / (state.scale || 1)
  moveSelectedBy(dxPx * k, dyPx * k)
}

export function rotateSelectedBy(deltaDeg = 0) {
  if (!selected.value) return
  selected.value.rot = (selected.value.rot + deltaDeg) % 360
}

export function rotateSelectedTo(deg = 0) {
  if (!selected.value) return
  selected.value.rot = Number(deg) % 360
}

// ---------- Z-order ----------
export function bringToFront(id = state.selectedId) {
  const idx = state.obstacles.findIndex(o => o.id === id)
  if (idx < 0) return
  const maxZ = state.obstacles.reduce((m, o) => Math.max(m, o.z ?? 0), 0)
  state.obstacles[idx].z = maxZ + 1
}

export function sendToBack(id = state.selectedId) {
  const idx = state.obstacles.findIndex(o => o.id === id)
  if (idx < 0) return
  const minZ = state.obstacles.reduce((m, o) => Math.min(m, o.z ?? 0), Infinity)
  const newZ = Number.isFinite(minZ) ? (minZ - 1) : 0
  state.obstacles[idx].z = newZ
}

// ---------- Edição via campos de texto (watchers migrados do pai) ----------
export function setEditsFromSelected() {
  const s = selected.value
  if (!s) {
    state.editLists.thicks = ''
    state.editLists.heights = ''
    state.editLists.gaps = ''
    return
  }
  const base = params.obstacles?.[s.type] || {}
  const th = s.local?.thicknesses?.length ? s.local.thicknesses
           : (base.thicknesses || (base.barThickness ? [base.barThickness] : []))
  const hh = s.local?.heights?.length ? s.local.heights
           : ((Array.isArray(base.heights) && base.heights.length ? base.heights
              : (Array.isArray(base.barHeights) && base.barHeights.length ? base.barHeights : [])))
  const gp = s.local?.gaps?.length ? s.local.gaps : (base.gaps || [])

  state.editLists.thicks  = formatListSemicolon(th, 2)
  state.editLists.heights = hh.length ? formatListSemicolon(hh, 2) : ''
  state.editLists.gaps    = gp.length ? formatListSemicolon(gp, 2) : ''
}



// Aplica o texto dos inputs ao objeto selecionado (sobrescrevendo o .local)
export function applyEditsToSelected({ precision = 2 } = {}) {
  const s = selected.value
  if (!s) return
  const th = state.editLists.thicks?.trim()  ? parseNumberList(state.editLists.thicks)   : []
  const hh = state.editLists.heights?.trim() ? parseNumberList(state.editLists.heights)  : []
  const gp = state.editLists.gaps?.trim()    ? parseNumberList(state.editLists.gaps)     : []
  const roundp = v => Number(Number.parseFloat(v).toFixed(precision))

  s.local = s.local || {}
  if (th.length) s.local.thicknesses = th.map(roundp); else delete s.local.thicknesses
  if (hh.length) s.local.heights     = hh.map(roundp); else delete s.local.heights
  if (gp.length) s.local.gaps        = gp.map(roundp); else delete s.local.gaps
}

// Mantém os inputs sincronizados quando o selecionado muda
watch(selected, () => setEditsFromSelected(), { immediate: true })

// ---------- Serialização / hidratação ----------
export function snapshot() {
  // Retorna um clone leve do estado relevante (para salvar/undo/redo)
  return {
    params: JSON.parse(JSON.stringify(params)),
    state: {
      ...state,
      obstacles: JSON.parse(JSON.stringify(state.obstacles)),
    },
  }
}

// Substitui obstáculos atuais pelo payload (carregar pista de JSON)
export function replaceObstacles(list = []) {
  state.obstacles.splice(0, state.obstacles.length, ...list.map(o => ({ ...o })))
}

// Hidratar tudo (opcional)
export function hydrate({ params: p, state: s } = {}) {
  if (p?.obstacles) params.obstacles = { ...params.obstacles, ...p.obstacles }
  if (p?.visual)    params.visual    = { ...params.visual,    ...p.visual }

  if (s) {
    state.scale     = s.scale     ?? state.scale
    state.view      = { ...(state.view || {}), ...(s.view || {}) }
    state.arenaMode = s.arenaMode ?? state.arenaMode
    state.free      = { ...(state.free || {}), ...(s.free || {}) }
    state.selectedId = s.selectedId ?? null
    replaceObstacles(asArray(s.obstacles))
  }
}

// ---------- Export em pacote (açúcar) ----------
export const actions = {
  select, clearSelection,
  addObstacle, removeSelected, cloneSelected,
  moveSelectedBy, moveSelectedByPx,
  rotateSelectedBy, rotateSelectedTo,
  bringToFront, sendToBack,
  setEditsFromSelected, applyEditsToSelected,
  replaceObstacles, hydrate, snapshot, reflowComboFromChanged,
}
